// src/pages/usa/colorado/Telluride.js
import "../../../App.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import BackOnly from "../../../components/BackOnly";
import ContactPopup from "../../../components/ContactPopup";
import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

import AffiliateBooking from "../../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../../components/affiliate/AffiliateTrip";

// ⭐ STRICT COMMAND: Telluride ALWAYS goes back to Colorado map
export const backRoute = "/map/colorado";

export default function Telluride() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly (NO LOOPS)
  useEffect(() => {
    if (!window.__tellurideBackRouteSet) {
      window.__tellurideBackRouteSet = true;
      navigate(".", { replace: true, state: { backRoute } });
    }
  }, [navigate]);

  return (

    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Telluride, Colorado</h1>

        <section className="travel-review">
          {/* LEFT COLUMN — TEXT */}
          <div className="travel-text">
            <h2>🏞️ Discover Telluride, Colorado</h2>

            <p>
              Telluride sits at 8,750 feet, with many trails reaching above
              10,000 — meaning thinner air, stronger sun, and faster fatigue.
              Hydration is important before arrival and throughout your stay.
              Ease into activity to let your body acclimate. Watch for signs of
              altitude sickness like headaches or dizziness, and descend if
              symptoms worsen.
            </p>

            {/* GETTING THERE */}
            <h3>🚗 Getting There</h3>

            <p>
              <strong>Getting There: Road vs. Air</strong>
              <br />
              If you're flying, <em>Telluride Regional Airport (TEX)</em> is
              closest, but limited in service.{" "}
              <em>Montrose Regional Airport (MTJ)</em>, about 1.5 hours away,
              offers more flight options and shuttle services into town. Driving
              from Denver takes 6.5–7 hours through scenic passes — ideal for
              creators hauling gear or chasing roadside photo ops.
              <br />
              I’ve been to Telluride twice, and both times I chose to drive.
              It’s a long haul, but absolutely worth it — the scenery alone
              makes the hours fly. If you’re planning to drive as well, keep in
              mind that it’s a long stretch, and fatigue or drowsiness can sneak
              up on you. I always have someone with me on long drives to help
              stay alert and share the driving.
            </p>

            {/* PERSONAL OBSERVATION */}
            <h3>🌟 My Personal Observation</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Bridal Veil Falls:</strong> One of my favorite
                  waterfalls in all of Colorado — dramatic, powerful, and tucked
                  into Telluride’s box canyon like a secret. I’ve mountain biked
                  up once and driven to the top another time. The road is narrow,
                  exposed, and has no guardrails. I do <em>not</em> recommend
                  driving to the top — it’s risky and not worth it unless
                  absolutely necessary.
                  <br />
                  The trail is about 2 miles round trip and offers incredible
                  views. I’m planning my third trip soon, and this time I want to
                  hike all the way to the base and feel the mist up close.
                  <br />
                  <a
                    href="https://www.google.com/maps?q=37.919274915600006,-107.76958807343446"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Bridal Veil Falls
                  </a>
                </div>
              </li>
            </ul>

            {/* HIDDEN GEMS */}
            <h3>⛰️ Hidden Gems in Telluride</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>The Free Gondola:</strong> Stunning alpine views at sunset.<br />
                  <a
                    href="https://www.google.com/maps?q=37.936011436898454, -107.81361093882087"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Free Gondola
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Kroger Via Ferrata:</strong> Cliffside traverse 500 ft above the valley.<br />
                  <a
                    href="https://www.google.com/maps?q=37.923913955832646, -107.76694174448701"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Kroger Via Ferrata
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Bear Creek Falls:</strong> Roaring waterfall at the canyon’s end.<br />
                  <a
                    href="https://www.google.com/maps?q=37.908409600153725, -107.81062360517126"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Bear Creek Falls
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Jud Wiebe Trail:</strong> Steep loop with panoramic views.<br />
                  <a
                    href="https://www.google.com/maps?q=37.941093976107446, -107.81236769059988"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Jud Wiebe Trail
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Tomboy Road:</strong> Historic mining route to a ghost town.<br />
                  <a
                    href="https://www.google.com/maps?q=37.94101837182691, -107.76443675797746"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Tomboy Road
                  </a>
                </div>
              </li>

              

              <li>
                <div className="place-block">
                  <strong>San Juan Outdoor Adventures:</strong> Guided Via Ferrata + alpine hikes.<br />
                  <a
                    href="https://www.google.com/maps?q=37.93680643982486, -107.80888551750346"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 San Juan Outdoor Adventures
                  </a>
                </div>
              </li>
            </ul>

            {/* PLACES TO STAY */}
            <h3>🛏️ Places to Stay in Telluride</h3>

            <p>
              I haven’t stayed overnight in Telluride myself — I usually drive
              back to Ouray or Montrose — but these are consistently top‑ranked:
            </p>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Madeline Hotel & Residences, Auberge Resorts Collection:</strong>
                  Luxury ski‑in/ski‑out.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>The Hotel Telluride:</strong> Lodge with hot tubs and dining.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Mountain Lodge Telluride:</strong> Cabins + condos.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>The Peaks Resort & Spa:</strong> Slopeside luxury.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Inn at Lost Creek:</strong> Ski‑in/ski‑out boutique.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Camel's Garden Hotel:</strong> Steps from gondola.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Ice House Suites:</strong> Alpine lodge with après‑ski.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Beaumont Hotel (Ouray):</strong> Historic elegance.
                </div>
              </li>
            </ul>

            {/* AFFILIATE BLOCK */}
           <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
                       <div className="affiliate-block">
                         <AffiliateExpedia />
                         <AffiliateBooking />
                         <AffiliateTrip />
                       </div>

            {/* VIDEOS */}
            <h3>🎥 My Travel Videos</h3>

            <p>Here are some videos I took from Bridal Veil Falls and Via Ferrata.</p>

            <p>
              <a
                href="https://youtu.be/twI-K7lbsEI?si=4A6hb9M0WKdFpo8S"
                target="_blank"
                rel="noopener noreferrer"
              >
                Bridal Veil Falls
              </a>
              <br />
              <a
                href="https://youtu.be/Im35MEnN_co?si=DztGwwNw3IO0BhxT"
                target="_blank"
                rel="noopener noreferrer"
              >
                Via Ferrata
              </a>
            </p>
          </div>

          {/* RIGHT COLUMN — IMAGES */}
          <div className="travel-image-column">
            <a
              href="https://youtu.be/twI-K7lbsEI?si=4A6hb9M0WKdFpo8S"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/telluride/bvf.jpg"
                alt="Bridal Veil Falls Video"
              />
            </a>

            <a
              href="https://youtu.be/Im35MEnN_co?si=vL0kT7K5Rai8D9M4"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/telluride/vv.jpg"
                alt="Via Ferrata Video"
              />
            </a>

            <img src="/images/usa/colorado/telluride/bridalfromfar.jpg" alt="Bridal Veil Falls" />
            <img src="/images/usa/colorado/telluride/valley.jpg" alt="The Valley" />
            <img src="/images/usa/colorado/telluride/ingramfalls.jpg" alt="Ingram Falls" />
            <img src="/images/usa/colorado/telluride/trail.jpg" alt="Trail to Bridal Veil Falls" />
            <img src="/images/usa/colorado/telluride/thetrail.jpg" alt="Bridal Veil Falls Trail" />
            <img src="/images/usa/colorado/telluride/powerhouse.jpg" alt="Power House" />
          </div>
        </section>
      </div>

      {/* EMAIL BUTTON */}
      <div className="email-wrapper">
        <button className="email-link" onClick={() => setOpen(true)}>
          contact@thefindingspot.com
        </button>
      </div>

      {/* POPUP */}
      <ContactPopup isOpen={open} onClose={() => setOpen(false)} />

      <Footer />
    </>
  );
}
